import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { useDispatch, useSelector } from "react-redux";
import { Oval } from "react-loader-spinner";
import Button from "./Comon/Button/Button";
import Dropdown from "./Admin Panel/Dropdown/Dropdown";
import { addUser } from "../../redux/actions/user";
import {
  practiceHubDeleteIntegration,
  practiceHubGenerateIntegrationApiKey,
  practiceHubUpdateIntegration,
} from "../../core/axios/services";
import Headline from "./Comon/Headline/Headline";

const SURVEY_TYPE_OPTIONS = [
  { id: 1, name: "ChiroForm" },
  { id: 2, name: "ChiroForm +" },
  { id: 3, name: "ChiroForm (CP)" },
];

const getIntegrationFromPayload = (payload) => {
  return (
    payload?.user?.clinic?.practiceHubIntegration ??
    payload?.clinic?.practiceHubIntegration ??
    payload?.practiceHubIntegration ??
    null
  );
};

const getIdFromValue = (value) => {
  if (value == null) {
    return null;
  }

  if (typeof value === "object") {
    return value?.id ?? null;
  }

  const parsedValue = Number(value);
  return Number.isNaN(parsedValue) ? null : parsedValue;
};

const getDefaultSurveyTypeId = (integration) => {
  return (
    getIdFromValue(integration?.additionalData?.default_survey_type) ??
    getIdFromValue(integration?.additionalData?.defaultSurveyType) ??
    getIdFromValue(integration?.defaultSurveyType)
  );
};

const getDefaultSurveyLanguageId = (integration) => {
  return (
    getIdFromValue(integration?.additionalData?.default_survey_language) ??
    getIdFromValue(integration?.additionalData?.defaultSurveyLanguage) ??
    getIdFromValue(integration?.defaultSurveyLanguage)
  );
};

const getNormalizedFormData = (integration, languages = []) => {
  const surveyTypeId = getDefaultSurveyTypeId(integration);
  const surveyLanguageId = getDefaultSurveyLanguageId(integration);

  return {
    apiKey: integration?.apiKey ?? "",
    defaultSurveyType:
      SURVEY_TYPE_OPTIONS.find((item) => item?.id === surveyTypeId) ?? null,
    defaultSurveyLanguage:
      languages?.find((item) => item?.id === surveyLanguageId) ?? null,
  };
};

const PracticeHubOutgoingIntegration = ({ app, i18n, showGlobalSnackbar }) => {
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();

  const initialIntegration = user?.user?.clinic?.practiceHubIntegration ?? null;
  const languageOptions = user?.user?.clinic?.languages ?? [];
  const [hasIntegration, setHasIntegration] = useState(!!initialIntegration);
  const [formData, setFormData] = useState(
    getNormalizedFormData(initialIntegration, languageOptions)
  );
  const [errors, setErrors] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const getFieldErrors = (...keys) => {
    return keys.flatMap((key) => errors?.[key] ?? []);
  };

  useEffect(() => {
    const integration = user?.user?.clinic?.practiceHubIntegration;
    if (!integration || hasIntegration) {
      return;
    }

    setHasIntegration(true);
    setFormData(getNormalizedFormData(integration, languageOptions));
  }, [user, hasIntegration, languageOptions]);

  const handleGenerateApiKey = async () => {
    if (isGenerating) {
      return;
    }

    setIsGenerating(true);

    const response = await practiceHubGenerateIntegrationApiKey(user);
    if (response?.status === 200) {
      dispatch(addUser(response.data.data));
      setErrors(null);

      const integration = getIntegrationFromPayload(response?.data?.data);
      const normalizedData = getNormalizedFormData(integration, languageOptions);
      setFormData((prevState) => ({
        apiKey: normalizedData?.apiKey ?? prevState.apiKey,
        defaultSurveyType: hasIntegration
          ? prevState.defaultSurveyType
          : normalizedData?.defaultSurveyType ?? prevState.defaultSurveyType,
        defaultSurveyLanguage: hasIntegration
          ? prevState.defaultSurveyLanguage
          : normalizedData?.defaultSurveyLanguage ?? prevState.defaultSurveyLanguage,
      }));
      setHasIntegration(true);
      showGlobalSnackbar(i18n.t("saved"));
    } else {
      setErrors(response?.response?.data?.messages);
    }

    setIsGenerating(false);
  };

  const handleSave = async () => {
    if (isSaving) {
      return;
    }

    setIsSaving(true);

    const response = await practiceHubUpdateIntegration(
      {
        defaultSurveyType: formData?.defaultSurveyType?.id ?? null,
        defaultSurveyLanguage: formData?.defaultSurveyLanguage?.id ?? null,
      },
      user
    );

    if (response?.status === 200) {
      dispatch(addUser(response.data.data));
      setErrors(null);
      showGlobalSnackbar(i18n.t("saved"));
    } else {
      setErrors(response?.response?.data?.messages);
    }

    setIsSaving(false);
  };

  const handleDeleteIntegration = async () => {
    if (isDeleting) {
      return;
    }

    setIsDeleting(true);

    const response = await practiceHubDeleteIntegration(user);

    if (response?.status === 200) {
      dispatch(addUser(response.data.data));
      setErrors(null);
      setHasIntegration(false);
      setFormData(getNormalizedFormData(null, languageOptions));
      showGlobalSnackbar(i18n.t("saved"));
    } else {
      setErrors(response?.response?.data?.messages);
    }

    setIsDeleting(false);
  };

  const handleCopyKey = async () => {
    if (!formData?.apiKey) {
      return;
    }

    try {
      await navigator.clipboard.writeText(formData.apiKey);
      showGlobalSnackbar(i18n.t("linkCopied"));
    } catch (error) {
      showGlobalSnackbar(i18n.t("thereWasAnError"));
    }
  };

  return (
    <div className="practiceHubOutgoingIntegration">
      <p className="practiceHubIntegrationDescription">
        Generate a unique link. Go to PracticeHub {"->"} Settings {"->"} Integrations.
        Select the Chiropractic Outcomes app and paste the API Key link there.
      </p>

      {!hasIntegration && (
        <Box className={"buttonContainer"} onClick={handleGenerateApiKey}>
          <Button
            text={
              isGenerating ? (
                <Oval
                  height={20}
                  width={20}
                  color="#fff"
                  visible={true}
                  ariaLabel="oval-loading"
                  secondaryColor="#fff"
                  strokeWidth={2}
                  strokeWidthSecondary={2}
                />
              ) : (
                "Generate API Key"
              )
            }
            mode={"small"}
          />
        </Box>
      )}

      {hasIntegration && (
        <>
          <div className={"integrationDropdown"}>
            <div className="practiceHubOutgoingApiKeyHeader">
              <span className="dropdownLable">{i18n.t("APIKey")}</span>
              <span className="practiceHubOutgoingApiKeyLink" onClick={handleCopyKey}>
                Copy API Key
              </span>
            </div>
            <div className="practiceHubOutgoingApiKeyInput">
              <div className="practiceHubOutgoingApiKeyValue">
                {formData?.apiKey || "-"}
              </div>
              <div
                className="practiceHubOutgoingApiKeyRefresh"
                onClick={handleGenerateApiKey}
                title={"Refresh API key"}
              >
                {isGenerating ? "..." : <AutorenewIcon style={{ fontSize: 18 }} />}
              </div>
            </div>
            <div className="explanation">
              If needed, click refresh to generate a new API key.
            </div>
            {getFieldErrors("apiKey").map((item, index) => (
              <div key={index} className={"errorMessage"}>
                {item}
              </div>
            ))}
          </div>


          <div style={{ marginBottom: "20px",marginTop: "30px" }}>
            <Headline
                text={i18n.t("automateProcess")}
                type={"Headline-medium"}
            />
          </div>

          <Box
            style={{ marginTop: "20px", marginBottom: "20px" }}
            className={"integrationDropdownWrapper"}
          >
            <div className={"integrationDropdown"}>
              <span className="dropdownLable">{i18n.t("defaultSurveyType")}</span>
              <Dropdown
                type={"statistics"}
                setSelectedOption={(option) =>
                  setFormData({ ...formData, defaultSurveyType: option })
                }
                selectedOption={formData?.defaultSurveyType}
                options={SURVEY_TYPE_OPTIONS}
                app={app}
                placeholder="Select"
              />
              {getFieldErrors("defaultSurveyType", "defaultSurveyType.id").map(
                (item, index) => (
                  <div key={index} className={"errorMessage"}>
                    {item}
                  </div>
                )
              )}
            </div>

            <div className={"integrationDropdown"}>
              <span className="dropdownLable">{i18n.t("defaultSurveyLanguage")}</span>
              <Dropdown
                type={"statistics"}
                setSelectedOption={(option) =>
                  setFormData({ ...formData, defaultSurveyLanguage: option })
                }
                selectedOption={formData?.defaultSurveyLanguage}
                options={languageOptions}
                app={app}
                placeholder="Select"
              />
              {getFieldErrors("defaultSurveyLanguage", "defaultSurveyLanguage.id").map(
                (item, index) => (
                  <div key={index} className={"errorMessage"}>
                    {item}
                  </div>
                )
              )}
            </div>
          </Box>

          <Box className={"buttonContainer"} onClick={handleSave} style={{marginTop:'30px'}}>
            <Button
              text={
                isSaving ? (
                  <Oval
                    height={20}
                    width={20}
                    color="#fff"
                    visible={true}
                    ariaLabel="oval-loading"
                    secondaryColor="#fff"
                    strokeWidth={2}
                    strokeWidthSecondary={2}
                  />
                ) : (
                  "Save"
                )
              }
              mode={"small"}
            />
          </Box>



          <div style={{ marginBottom: "20px",marginTop: "30px" }}>
            <Headline
                text={'Stop sync'}
                type={"Headline-medium"}
            />
          </div>
          <p className="practiceHubIntegrationDescription">
            To disable the PracticeHub integration, and end syncing, click here
          </p>

          <Box
              style={{ marginTop: "10px", marginBottom: "40px" }}
              className={"buttonContainer"}
              onClick={handleDeleteIntegration}
          >
            <Button
                text={
                  isDeleting ? (
                      <Oval
                          height={20}
                          width={20}
                          color="#fff"
                          wrapperStyle={{}}
                          wrapperClass=""
                          visible={true}
                          ariaLabel="oval-loading"
                          secondaryColor="#fff"
                          strokeWidth={2}
                          strokeWidthSecondary={2}
                      />
                  ) : (
                      "Stop Sync"
                  )
                }
                mode={"small"}
                color={"red"}
            />
          </Box>
        </>
      )}
    </div>
  );
};

export default PracticeHubOutgoingIntegration;
